import { Component, Element, h, Host, type JSX, Prop } from "@stencil/core";
import { navContentStore } from "@phn/state/nav-content-store";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import { sendDataLayerEvent } from "@phn/services/analytics/data-layer-event-sender";
import analyticsManager from "@phn/services/analytics/analytics-manager";
import type { Series } from "../../../../shared/entities/content";
import { getModelsRoutingKey } from "../../../../shared/entities/routing-key";
import { navStateStore } from "../../../../shared/state/nav-state-store";
import {
  type ClickElementType,
  ClickElementTypes,
  EventActions,
  MenuItemVariant,
} from "../../../../shared/utility/constants";
import { addRoute } from "../../../../shared/services/routing-manager";
import { getSeriesImage } from "../../../../shared/utility/navi-state";

@Component({
  tag: "phn-nd-models",
  styleUrl: "models.scss",
  assetsDirs: ["series-assets"],
  scoped: true,
})
export class Models {
  @Prop() breakpoint!: number;

  @Prop() showHeadline = false;

  @Element() el!: HTMLPhnNdModelsElement;

  private async waitForComponentsToLoad() {
    await componentsOnReady(this.el, new Timeout(5000));
  }

  componentWillLoad(): void {
    this.waitForComponentsToLoad();
  }

  private showMenuWithModels(series: Series): void {
    addRoute([series.id]);
  }

  private getImageSource(image: string): string {
    return getSeriesImage(image, navStateStore.state.env);
  }

  private calculateAspectRatio(name: string): string {
    const isSUV = name === "Cayenne" || name === "Macan";
    const SUV_ASPECT_RATIO = "240/83";
    const DEFAULT_ASPECT_RATIO = "160/47";
    return isSUV ? SUV_ASPECT_RATIO : DEFAULT_ASPECT_RATIO;
  }

  private sendDataLayerEvent(
    modelSeries: Series,
    clickElementType: ClickElementType,
    elementId: string,
  ): void {
    sendDataLayerEvent({
      analyticsId: modelSeries.id,
      text: modelSeries.name,
      elementId,
      clickElementType,
      link: "", // TODO: link was always empty, change?
      customDatalayerProperties: {
        car: {
          engineType: analyticsManager.getEngineTypes(modelSeries),
        },
      },
      datalayerEventAction: EventActions.MODEL_RANGE_CLICK,
    });
  }

  render(): JSX.Element {
    const { models } = navContentStore.state;

    return (
      <Host>
        <ul>
          {models.series.map((modelsSeries) => (
            <li key={modelsSeries.id} class="models-item">
              <button
                class="series"
                type="button"
                onClick={() => {
                  this.sendDataLayerEvent(
                    modelsSeries,
                    ClickElementTypes.Button,
                    getModelsRoutingKey(modelsSeries.id),
                  );
                  this.showMenuWithModels(modelsSeries);
                }}
              >
                <phn-nd-menu-item
                  key={modelsSeries.id}
                  elementId={getModelsRoutingKey(modelsSeries.id)}
                  analyticsId={modelsSeries.id}
                  text={modelsSeries.name}
                  subtext={modelsSeries.tags.map((tag) => tag.text).join(" | ")}
                  variant={MenuItemVariant.Model}
                  isModels={true}
                  datalayerEventAction={EventActions.MODEL_RANGE_CLICK}
                >
                  <phn-model-tile
                    slot="model-area"
                    breakpoint={this.breakpoint}
                    modelSeries={modelsSeries}
                    aspectRatio={this.calculateAspectRatio(modelsSeries.name)}
                    srcSet={this.getImageSource(modelsSeries.image)}
                  />
                </phn-nd-menu-item>
              </button>
            </li>
          ))}
        </ul>
      </Host>
    );
  }
}
